import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Select } from '@dreamtree-org/twreact-ui';
import { useAppApi } from '@/lib/api';
import Can from '@rbac/Can';
import DataTable from '@/components/DataTable';
import PageShell from '@/components/layout/PageShell';
import PageHeader from '@/components/layout/PageHeader';

/**
 * "Knowledge Bases" list — mirrors DynamicToolsPage.jsx's structure (generic-CRUD-
 * backed list + a "New" button gated by the module's manage permission, click a row
 * to open its detail page). Source attach/ingest management lives entirely inside
 * KnowledgeBaseDetail.jsx, not here — this page only ever touches the
 * AppKnowledgeBase model itself via generic CRUD.
 */
const COLUMNS = [
  {
    key: 'name',
    label: 'Name',
    render: (kb) => (
      <div>
        <div className="font-medium text-primary-600">{kb.name}</div>
        <div className="text-secondary-500 text-xs max-w-xs truncate" title={kb.description}>{kb.description}</div>
      </div>
    ),
  },
  { key: 'chunk_count', label: 'Chunks', searchable: false, render: (kb) => kb.chunk_count || 0 },
  { key: 'embedding_model', label: 'Embedding model', render: (kb) => <span className="font-mono text-xs">{kb.embedding_model}</span> },
  {
    key: 'is_active',
    label: 'Status',
    searchable: false,
    render: (kb) => (
      <Badge variant={kb.is_active ? 'success' : 'secondary'} size="sm">
        {kb.is_active ? 'active' : 'disabled'}
      </Badge>
    ),
  },
];

export default function KnowledgeBasesPage() {
  const { fetchApi } = useAppApi();
  const nav = useNavigate();
  const [bases, setBases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [apps, setApps] = useState([]);
  const [applicationId, setApplicationId] = useState('');

  const reload = useCallback(() => {
    setLoading(true);
    return fetchApi('crud', { model: 'AppKnowledgeBase', action: 'list', limit: 100 })
      .then((res) => setBases((Array.isArray(res) ? res : res?.data) || []))
      .catch((ex) => setErr((ex.response && ex.response.data && ex.response.data.error) || ex.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    reload();
    fetchApi('applicationsList')
      .then((res) => setApps((Array.isArray(res) ? res : res?.data) || []))
      .catch(() => setApps([]));
  }, [reload]);

  const appOptions = useMemo(() => [
    { label: 'All applications', value: '' },
    ...apps.map((a) => ({ label: a.name, value: String(a.id) })),
  ], [apps]);

  const rows = useMemo(() => {
    let filtered = bases;
    if (applicationId) filtered = filtered.filter((item) => item.application_id == null || item.application_id === Number(applicationId));
    return filtered;
  }, [bases, applicationId]);

  return (
    <PageShell className="space-y-3">
      <PageHeader
        title="Knowledge Bases"
        subtitle={<>{bases.length} knowledge base{bases.length === 1 ? '' : 's'}{rows.length !== bases.length ? ` · ${rows.length} shown` : ''} — attach one to an agent to ground its answers in your own documents/data.</>}
        actions={(
          <Can module="knowledge" action="author">
            <Button size="sm" onClick={() => nav('/app-knowledge-base/new')}>New knowledge base</Button>
          </Can>
        )}
      />

      <DataTable
        data={rows}
        columns={COLUMNS}
        loading={loading}
        error={err}
        emptyText="No knowledge bases yet."
        search={{ placeholder: 'Search knowledge bases…', keys: ['name', 'description', 'embedding_model'] }}
        toolbar={(
          <div className="w-48 sm:w-56"><Select options={appOptions} value={applicationId} onChange={setApplicationId} placeholder="All applications" searchable /></div>
        )}
        onRowClick={(kb) => nav(`/app-knowledge-base/${kb.id}`)}
        onReload={reload}
      />
    </PageShell>
  );
}
